import React, {useEffect, useRef, useState} from 'react';
import classes from "./AudioPlayer.module.css";
import soundOnIcon from "../../../assets/sound-on.svg";
import playIcon from "../../../assets/play.svg";
import pauseIcon from "../../../assets/pause.svg";
import minSoundIcon from "../../../assets/sound-minimum.svg";
import Hls from "hls.js";
import noCoverImage from "../../../assets/no_cover.webp";

const AudioPlayer = ({backendUrl = '/api'}) => {
    const [play, setPlay] = useState(false);
    const [volume, setVolume] = useState(100);
    const [currentTrack, setCurrentTrack] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);

    const audioRef = useRef(null);
    const titleRef = useRef(null);
    const hlsRef = useRef(null);
    const playAttemptRef = useRef(null);
    const lastTrackRef = useRef(null);
    const retryTimeoutRef = useRef(null);

    const extractTrackName = (url) => {
        const match = url.match(/(.+?)_\d+k_\d+\.ts$/u);
        return match ? match[1] : null;
    };

    function isIosSafari() {
        const userAgent = window.navigator.userAgent;
        const result = /iPhone|iPad|iPod/i.test(userAgent) && /Safari/i.test(userAgent) && !/Chrome/i.test(userAgent);
        return result;
    };

    const initializeHLS = (streamUrl) => {
        lastTrackRef.current = null;
        setCurrentTrack(null);

        if (hlsRef.current) {
            hlsRef.current.destroy();
        }

        hlsRef.current = new Hls({
            enableWorker: true,
            lowLatencyMode: true,
            liveSyncDurationCount: 8,
            liveMaxLatencyDurationCount: 10,
            maxBufferLength: 28,
            maxMaxBufferLength: 28,
            maxBufferSize: 3 * 1000 * 1000,
            maxBufferHole: 0.5,
            liveDurationInfinity: true,

            manifestLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 5000,
                    maxLoadTimeMs: 10000,
                    timeoutRetry: {
                        maxNumRetry: 13,
                        retryDelayMs: 2000,
                        maxRetryDelayMs: 2000
                    },
                    errorRetry: {
                        maxNumRetry: 13,
                        retryDelayMs: 2000,
                        maxRetryDelayMs: 2000
                    }
                }
            },
            playlistLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 5000,
                    maxLoadTimeMs: 10000,
                    timeoutRetry: {
                        maxNumRetry: 13,
                        retryDelayMs: 2000,
                        maxRetryDelayMs: 2000
                    },
                    errorRetry: {
                        maxNumRetry: 13,
                        retryDelayMs: 2000,
                        maxRetryDelayMs: 2000
                    }
                }
            },
            fragLoadPolicy: {
                default: {
                    maxTimeToFirstByteMs: 2300,
                    maxLoadTimeMs: 2300,
                    timeoutRetry: {
                        maxNumRetry: 13,
                        retryDelayMs: 2000,
                        maxRetryDelayMs: 2000
                    },
                    errorRetry: {
                        maxNumRetry: 13,
                        retryDelayMs: 2000,
                        maxRetryDelayMs: 2000
                    }
                }
            }
        });


        hlsRef.current.loadSource(streamUrl);
        hlsRef.current.attachMedia(audioRef.current);

        hlsRef.current.on(Hls.Events.FRAG_CHANGED, (event, data) => {
            const trackName = extractTrackName(data.frag.relurl);
            if (trackName && trackName !== lastTrackRef.current) {
                lastTrackRef.current = trackName;
                setCurrentTrack({ name: trackName });
            }
        });

        hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {
            const attemptPlay = async () => {
                try {
                    await audioRef.current.play();
                    setIsLoading(false);
                } catch (error) {
                    console.warn('Play attempt failed, retrying in 1 second...', error);
                    playAttemptRef.current = setTimeout(attemptPlay, 1000);
                }
            };
            attemptPlay();
        });

        hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
            console.error('HLS error:', data);
            if (data.fatal) {
                if (audioRef.current) {
                    audioRef.current.pause();
                    audioRef.current.src = "";
                }
                if (hlsRef.current) {
                    hlsRef.current.detachMedia();
                }
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        console.log('Network error occurred, attempting to reload stream...');
                        if (retryTimeoutRef.current) {
                            clearTimeout(retryTimeoutRef.current);
                        }
                        retryTimeoutRef.current = setTimeout(() => {
                            console.log('Retrying stream connection...');
                            hlsRef.current.destroy();
                            initializeHLS(streamUrl);
                        }, 1000);
                        break;
                    case Hls.ErrorTypes.MEDIA_ERROR:
                        hlsRef.current.recoverMediaError();
                        break;
                    default:
                        hlsRef.current.destroy();
                        retryTimeoutRef.current = setTimeout(() => {
                            console.log('Retrying stream connection after fatal error...');
                            initializeHLS(streamUrl);
                        }, 1000);
                        break;
                }
            }
        });
    };

    useEffect(() => {
        const audio = audioRef.current;

        const handlePlay = () => setPlay(true);
        const handlePause = () => setPlay(false);
        const handleWaiting = () => {
            setIsLoading(true);
            if (hlsRef.current) {
                hlsRef.current.stopLoad();
                hlsRef.current.startLoad();
            } else {
                audioRef.current.load();
            }
        };
        const handleCanPlay = () => setIsLoading(false);

        if (audio) {
            audio.addEventListener('play', handlePlay);
            audio.addEventListener('pause', handlePause);
            audio.addEventListener('waiting', handleWaiting);
            audio.addEventListener('canplay', handleCanPlay);
        }

        return () => {
            if (audio) {
                audio.removeEventListener('play', handlePlay);
                audio.removeEventListener('pause', handlePause);
                audio.removeEventListener('waiting', handleWaiting);
                audio.removeEventListener('canplay', handleCanPlay);
            }
        };
    }, []);

    useEffect(() => {
        if (play) {
            setIsLoading(true);

            if (playAttemptRef.current) {
                clearTimeout(playAttemptRef.current);
            }

            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }

            const streamUrl = `${backendUrl}/stream.m3u8`;

            if (Hls.isSupported()) {
                initializeHLS(streamUrl);
            } else if (audioRef.current.canPlayType('application/vnd.apple.mpegurl')) {
                audioRef.current.src = streamUrl;
                audioRef.current.play().catch(error => {
                    console.error('Native player error:', error);
                    setIsLoading(false);
                    retryTimeoutRef.current = setTimeout(() => {
                        console.log('Retrying native player connection...');
                        audioRef.current.load();
                        audioRef.current.play().catch(console.error);
                    }, 1000);
                });
            }
        } else {
            if (hlsRef.current) {
                hlsRef.current.stopLoad();
            }
            if (playAttemptRef.current) {
                clearTimeout(playAttemptRef.current);
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
            audioRef.current?.pause();
            setIsLoading(false);
        }

        return () => {
            if (hlsRef.current) {
                hlsRef.current.stopLoad();
            }
            if (playAttemptRef.current) {
                clearTimeout(playAttemptRef.current);
            }
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
            }
        };
    }, [backendUrl, play]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume / 100;
        }
    }, [volume]);

    useEffect(() => {
        if (titleRef.current) {
            const containerWidth = titleRef.current.parentElement.offsetWidth;
            const contentWidth = titleRef.current.scrollWidth;
            setShouldScroll(contentWidth > containerWidth);
        }
    }, [currentTrack]);

    const togglePlay = () => {
        if (isLoading) return;
        const newPlayState = !play;
        setPlay(newPlayState);

        if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = newPlayState ? 'playing' : 'paused';
        }
    };

    const handleVolumeChange = (e) => {
        if (isIosSafari() && volume !== 100) return setVolume(100);
        if (isIosSafari()) return;
        setVolume(e.target.value);
    };

    useEffect(() => {
        if ('mediaSession' in navigator) {
            navigator.mediaSession.setActionHandler('play', () => {
                setPlay(true);
            });

            navigator.mediaSession.setActionHandler('pause', () => {
                setPlay(false);
            });

            return () => {
                navigator.mediaSession.setActionHandler('play', null);
                navigator.mediaSession.setActionHandler('pause', null);
            };
        }
    }, []);

    useEffect(() => {
        if ('mediaSession' in navigator) {
            const [artist, trackName] = currentTrack?.name
                ? currentTrack.name.split(' — ')
                : ['Unknown Artist', 'Unknown Track'];

            const coverUrl = `${backendUrl}/${currentTrack?.name}_cover.webp`;
            const fallbackCover = noCoverImage;

            const checkImage = (url) =>
                new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => resolve(true);
                    img.onerror = () => resolve(false);
                    img.src = url;
                });

            const setMetadata = async () => {
                const isCoverAvailable = await checkImage(coverUrl);
                navigator.mediaSession.metadata = new MediaMetadata({
                    title: trackName || 'Unknown Track',
                    artist: artist || 'Unknown Artist',
                    artwork: [
                        {
                            src: isCoverAvailable ? coverUrl : fallbackCover,
                            sizes: '512x512',
                            type: 'image/jpeg',
                        },
                    ],
                });
            };

            setMetadata();

            navigator.mediaSession.playbackState = play ? 'playing' : 'paused';
        }
    }, [currentTrack, backendUrl, noCoverImage, play]);


    useEffect(() => {
        if (!window?.parent) return;
        if (play) {
            window.parent.postMessage({ type: 'radioPlayerState', state: 'play' }, '*');
        } else {
            window.parent.postMessage({ type: 'radioPlayerState', state: 'pause' }, '*');
        }
    }, [play]);

    useEffect(() => {
        return () => {
            if (hlsRef.current) {
                hlsRef.current.destroy();
            }
        };
    }, []);

    return (
        <div className={classes.wrapper}>
            <div className={classes.container}>
                <audio
                    ref={audioRef}
                    preload="auto"
                    controls={false}
                    crossOrigin="anonymous"
                />
                <div className={classes.image_block}>
                    <div className={classes.image_container}>
                        <img src={`${backendUrl}/${currentTrack?.name}_cover.webp`}
                             onError={(e) => (e.currentTarget.src = noCoverImage)}
                             alt="track image"/>
                    </div>
                </div>
                <div className={`${classes.title_block} ${shouldScroll ? classes.scroll : ''}`}>
                    <h3
                        ref={titleRef}
                        className={classes.track_name}
                    >
                        {currentTrack?.name || 'Press Button Play'}
                    </h3>
                </div>
                <div className={classes.progress_block}>
                    <div className={classes.time_line}>
                        <span>Live Stream</span>
                    </div>
                </div>
                <div className={classes.play_pause_block}>
                    <button onClick={togglePlay}>
                        <img src={play ? pauseIcon : playIcon} alt="play-pause-icon"/>
                    </button>
                </div>
                <div className={classes.sound_level_switch}>
                    <img src={minSoundIcon} alt="min-sound-icon"/>
                    <input
                        type="range"
                        min="0"
                        max="100"
                        value={volume}
                        onChange={handleVolumeChange}
                        className={classes.volume_slider}
                    />
                    <img src={soundOnIcon} alt="max-sound-icon"/>
                </div>
            </div>
        </div>
    );
};

export default AudioPlayer;